import { useMutation } from "@tanstack/react-query";
import { updateTaskFields, updateItemFields } from "@repo/shared-components";
import { useToast } from "@repo/ui";
import { fileApi } from "~/api";
import { useTabWindowsActions } from "~/pages/dashboard/task/tab-windows/hooks/use-tab-windows-context";
import type { Item } from "~/types/api";

type UpdateParams = {
  fields: { fieldId: number; value: string }[];
};

type UpdateTaskParams = UpdateParams & {
  taskId: number;
};

type UpdateItemParams = UpdateParams & {
  itemId: number;
};

type Props = {
  items: Item[];
  windowId?: string;
  onSuccess: (fieldIds: number[]) => void;
};

export const useFieldMutations = ({ items, windowId, onSuccess }: Props) => {
  const { showToast } = useToast();
  const { updateDetailViewForWindow } = useTabWindowsActions();

  const updateTaskMutation = useMutation({
    mutationFn: ({ taskId, fields }: UpdateTaskParams) => updateTaskFields(fileApi, taskId, fields),
    onSuccess: async (data, variables) => {
      if (windowId) await updateDetailViewForWindow(windowId, undefined, true);
      onSuccess(variables.fields.map((f) => f.fieldId));
    },
    onError: (error) => {
      showToast("error", "Error", "Failed to update task fields. Please try again.");
      console.error("Failed to update task fields:", error);
    },
    meta: {
      errorTitle: "Failed to update task field",
    },
  });

  const updateItemMutation = useMutation({
    mutationFn: ({ itemId, fields }: UpdateItemParams) => {
      const item = items.find((i) => i.id === itemId);
      if (!item) throw new Error("Item not found");
      return updateItemFields(fileApi, item, fields);
    },
    onSuccess: async (data, variables) => {
      if (windowId) await updateDetailViewForWindow(windowId, undefined, true);
      onSuccess(variables.fields.map((f) => f.fieldId));
    },
    onError: (error) => {
      showToast("error", "Error", "Failed to update item fields. Please try again.");
      console.error("Failed to update item fields:", error);
    },
    meta: {
      errorTitle: "Failed to update item field",
    },
  });

  return {
    updateTaskMutation,
    updateItemMutation,
    isSaving: updateItemMutation.isPending || updateTaskMutation.isPending,
  };
};
